import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const EMPTY_EVENT = {
    title: "", date: "", location: "", description: "", rsvp_url: "",
    is_active: true,
};

export const EventEditorDialog = ({ open, onOpenChange, editing, onSaved }) => {
    const [form, setForm] = useState(EMPTY_EVENT);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(editing ? {
            title: editing.title || "",
            date: editing.date || "",
            location: editing.location || "",
            description: editing.description || "",
            rsvp_url: editing.rsvp_url || "",
            is_active: editing.is_active !== false,
        } : EMPTY_EVENT);
    }, [open, editing]);

    const saveEvent = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editing) {
                await api.put(`/admin/events/${editing.id}`, form);
                toast.success("Event updated.");
            } else {
                await api.post("/admin/events", form);
                toast.success("Event created.");
            }
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-brand-navy dark:text-slate-100">{editing ? "Edit event" : "Create new event"}</DialogTitle>
                    <DialogDescription>{editing ? "Update the event details." : "This event will appear on the Community page under 'Upcoming Events'."}</DialogDescription>
                </DialogHeader>
                <form onSubmit={saveEvent} className="space-y-4">
                    <div>
                        <Label>Title *</Label>
                        <Input data-testid="admin-event-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Bilingual Resume Workshop" className="mt-2" />
                    </div>
                    <div>
                        <Label>Date *</Label>
                        <Input data-testid="admin-event-date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="e.g. Jan 18, 2026 or 2026-01-18 6pm" className="mt-2" />
                        <p className="text-xs text-brand-muted mt-1">Free-form — enter it exactly how you want it to appear on the website.</p>
                    </div>
                    <div>
                        <Label>Location</Label>
                        <Input data-testid="admin-event-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Atlanta Public Library — Downtown" className="mt-2" />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea data-testid="admin-event-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What is this event about?" className="mt-2" />
                    </div>
                    <div>
                        <Label>RSVP URL</Label>
                        <Input data-testid="admin-event-rsvp" value={form.rsvp_url} onChange={(e) => setForm({ ...form, rsvp_url: e.target.value })} placeholder="https://... (Eventbrite, Google Form, etc.)" className="mt-2" />
                        <p className="text-xs text-brand-muted mt-1">Leave blank to route "RSVP" to the contact page.</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-brand-text dark:text-slate-200">
                        <input
                            type="checkbox"
                            checked={form.is_active}
                            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                            data-testid="admin-event-active"
                        />
                        Visible on public site
                    </label>
                    <DialogFooter>
                        <button type="submit" disabled={saving} data-testid="admin-event-save" className="btn-navy disabled:opacity-60">
                            {saving ? <><Loader2 size={18} className="mr-2 animate-spin" /> Saving...</> : "Save event"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EventEditorDialog;
